import {
  useHierarchicalMenu,
  UseHierarchicalMenuProps,
} from 'react-instantsearch';
import { HierarchicalMenuItem } from 'instantsearch.js/es/connectors/hierarchical-menu/connectHierarchicalMenu';
import { useTranslation } from 'next-i18next';
import { ArrowLeftIcon } from '@phosphor-icons/react';

import SimpleLink from '_components/UI/SimpleLink';
import { EventName, trackNavClick } from '@utils/gtm/ga4/trackNavEvent';
import { PRODUCTS, PRODUITS } from '@utils/constants';

import styles from './CategoryNav.module.scss';

export type CategoryNavProps = UseHierarchicalMenuProps & {
  locale?: string;
};

const cleanUrl = (url: string) =>
  url.replace(/\/(en|fr)\/\1\//, '/$1/').replace(/\?page=\d+/, '');

const getRefinedPath = (
  items: HierarchicalMenuItem[]
): HierarchicalMenuItem[] => {
  const path: HierarchicalMenuItem[] = [];
  let level: HierarchicalMenuItem[] | undefined = items;

  while (level && level.length > 0) {
    const refined: HierarchicalMenuItem | undefined = level.find(
      (item: HierarchicalMenuItem) => item.isRefined
    );
    if (!refined) break;
    path.push(refined);
    level = refined.data ?? undefined;
  }

  return path;
};

const CategoryNav = (props: CategoryNavProps) => {
  const { locale, ...hierarchicalMenuProps } = props;
  const { items, createURL } = useHierarchicalMenu(hierarchicalMenuProps);
  const { t } = useTranslation(['searchResults']);

  const refinedPath = getRefinedPath(items);
  const current = refinedPath[refinedPath.length - 1];

  // `rootPath` (the localized "Products" root) is stripped by
  // useHierarchicalMenu, so `items` already starts at the top-level
  // categories: every entry above the current one is a real ancestor.
  const ancestors = refinedPath.slice(0, -1);

  const siblings =
    ancestors.length > 0 ? ancestors[ancestors.length - 1].data ?? [] : items;
  const subcategories = current
    ? current.data?.length
      ? current.data
      : siblings.filter((sibling) => sibling.value !== current.value)
    : items;

  const rootHref = `/${locale ?? 'en'}/${
    locale === 'fr' ? PRODUITS : PRODUCTS
  }`;

  const handleClick = (label: string, url: string) => {
    trackNavClick({
      data: {
        eventName: EventName.clickCategoryNavigation,
        linkUrl: url ?? null,
        linkText: label,
      },
    });
  };

  return (
    <nav className={styles.categoryNav}>
      {current && (
        <div className={styles.backLinks}>
          <SimpleLink
            href={rootHref}
            variant="primary"
            size="small"
            className={styles.backLink}
            rel="prefetch"
            onClick={() =>
              handleClick(t('search.facets.allCategories'), rootHref)
            }
          >
            <ArrowLeftIcon size={20} className={styles.backIcon} />
            {t('search.facets.allCategories')}
          </SimpleLink>

          {ancestors.map((ancestor, index) => {
            // Toggling a hierarchical refinement with an ancestor's own value walks
            // it up one extra level (algoliasearch-helper's toggle-up logic assumes
            // the value passed in is the currently active leaf), so build the URL
            // from the next level down instead, same as InstantSearch's breadcrumb
            // connector does via shiftItemsValues.
            const url = cleanUrl(createURL(refinedPath[index + 1].value));
            return (
              <SimpleLink
                key={ancestor.value}
                href={url}
                variant="primary"
                size="small"
                className={styles.backLink}
                rel="prefetch"
                onClick={() => handleClick(ancestor.label, url)}
              >
                <ArrowLeftIcon size={20} className={styles.backIcon} />
                {ancestor.label}
              </SimpleLink>
            );
          })}
        </div>
      )}

      {subcategories.length > 0 && (
        <div className={styles.currentGroup}>
          {current && (
            <span className={styles.currentLabel}>{current.label}</span>
          )}

          <div className={styles.subcategories}>
            <ul className={styles.subcategoryList}>
              {subcategories.map((item) => {
                const url = cleanUrl(createURL(item.value));
                return (
                  <li key={item.value} className={styles.subcategoryItem}>
                    <a
                      href={url}
                      rel="prefetch"
                      className={styles.subcategoryLink}
                      onClick={() => handleClick(item.label, url)}
                    >
                      <span className={styles.subcategoryLabel}>
                        {item.label}
                      </span>
                      <span className={styles.subcategoryCount}>
                        {item.count}
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </nav>
  );
};

export default CategoryNav;
